/**
 * PermitHeatmap.tsx
 *
 * Displays permit activity as a heatmap around a subject property.
 * Uses Google Maps with HeatmapLayer for permit density visualization.
 *
 * Phase 49-03: Tab 3 permit activity heatmap with luxury styling.
 */

import React, { useMemo, useCallback, useRef } from 'react';
import { GoogleMap, Marker, HeatmapLayer } from '@react-google-maps/api';
import { useGoogleMaps } from '../../providers/GoogleMapsProvider';
import { MapPin, Loader2, AlertTriangle, Thermometer } from 'lucide-react';
import { SectionTitle, GoldBadge } from '../Luxury';

interface HeatmapPoint {
    lat: number;
    lng: number;
    weight: number;
}

interface PermitIntelligence {
    renovation_ratio?: number | null;
    permit_lag_months?: number | null;
    permit_lag_correlation?: number | null;
    permit_density_per_1000?: number | null;
    classification_breakdown?: Record<string, number> | null;
}

interface PermitHeatmapProps {
    latitude: number;
    longitude: number;
    permitData?: HeatmapPoint[];
    nearbyPermitCount?: number;
    densityClassification?: string;
    isLoading?: boolean;
    error?: string;
    className?: string;
    permitIntelligence?: PermitIntelligence | null;
}

// Dark theme map style for luxury look
const darkMapStyle: google.maps.MapTypeStyle[] = [
    { elementType: 'geometry', stylers: [{ color: '#1d1d1d' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#1d1d1d' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4a5568' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2d2d2d' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1d1d1d' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1d1d1d' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#4a5568' }] },
];

// Density classification config
const densityConfig: Record<string, { label: string; color: string; description: string; badgeVariant: 'gold' | 'ssot' | 'off-market' }> = {
    hot_spot: {
        label: 'Hot Spot',
        color: 'text-red-400',
        description: 'Very high permit activity - established investment area',
        badgeVariant: 'gold',
    },
    above_avg: {
        label: 'Above Average',
        color: 'text-amber-400',
        description: 'Active permit area - moderate competition',
        badgeVariant: 'gold',
    },
    average: {
        label: 'Average',
        color: 'text-stone-700',
        description: 'Typical permit activity for this market',
        badgeVariant: 'off-market',
    },
    below_avg: {
        label: 'Below Average',
        color: 'text-blue-400',
        description: 'Lower activity - potential early opportunity',
        badgeVariant: 'off-market',
    },
    cold_spot: {
        label: 'Cold Spot',
        color: 'text-emerald',
        description: 'Value-add opportunity - ahead of the wave',
        badgeVariant: 'ssot',
    },
};

function MapLoading() {
    return (
        <div className="w-full h-full flex items-center justify-center bg-graphite rounded-lg">
            <div className="text-center">
                <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto mb-2" />
                <p className="text-stone-700 text-sm">Loading permit data...</p>
            </div>
        </div>
    );
}

function MapError({ message }: { message: string }) {
    return (
        <div className="w-full h-full flex items-center justify-center bg-graphite rounded-lg">
            <div className="text-center">
                <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-amber-400 text-sm">{message}</p>
            </div>
        </div>
    );
}

/**
 * PermitHeatmap - Visualizes permit activity around subject property
 *
 * Features:
 * - Google Maps with dark theme
 * - HeatmapLayer for permit density
 * - Property marker at subject location
 * - Nearby permit count display
 * - Density classification (hot_spot, cold_spot, etc.)
 * - Legend with interpretation
 */
export const PermitHeatmap: React.FC<PermitHeatmapProps> = ({
    latitude,
    longitude,
    permitData = [],
    nearbyPermitCount = 0,
    densityClassification = 'average',
    isLoading = false,
    error,
    className = '',
    permitIntelligence,
}) => {
    const { isLoaded, loadError } = useGoogleMaps();
    const mapRef = useRef<google.maps.Map | null>(null);

    const center = useMemo(() => ({ lat: latitude, lng: longitude }), [latitude, longitude]);

    const heatmapData = useMemo(() => {
        if (!isLoaded || !window.google) return [];
        return permitData.map(point => ({
            location: new google.maps.LatLng(point.lat, point.lng),
            weight: point.weight,
        }));
    }, [permitData, isLoaded]);

    const onMapLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;
    }, []);

    const mapOptions: google.maps.MapOptions = {
        styles: darkMapStyle,
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'cooperative',
    };

    const config = densityConfig[densityClassification] || densityConfig.average;

    // Handle loading and error states
    if (loadError) {
        return <MapError message="Failed to load Google Maps" />;
    }

    if (!isLoaded) {
        return <MapLoading />;
    }

    if (error) {
        return <MapError message={error} />;
    }

    return (
        <div className={`bg-charcoal p-6 rounded-lg border border-slate-dark ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <SectionTitle icon={<Thermometer className="w-5 h-5" />}>
                    Permit Activity Heatmap
                </SectionTitle>
                <GoldBadge variant={config.badgeVariant}>
                    {config.label}
                </GoldBadge>
            </div>

            {/* Map Container */}
            <div className="relative h-[300px] rounded-lg overflow-hidden border border-slate-dark">
                {isLoading ? (
                    <MapLoading />
                ) : (
                    <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={center}
                        zoom={15}
                        options={mapOptions}
                        onLoad={onMapLoad}
                    >
                        {/* Heatmap Layer */}
                        {heatmapData.length > 0 && (
                            <HeatmapLayer
                                data={heatmapData}
                                options={{
                                    radius: 30,
                                    opacity: 0.7,
                                    gradient: [
                                        'rgba(0, 255, 0, 0)',      // Transparent
                                        'rgba(0, 255, 0, 0.5)',    // Green
                                        'rgba(255, 255, 0, 0.7)',  // Yellow
                                        'rgba(255, 128, 0, 0.8)',  // Orange
                                        'rgba(255, 0, 0, 1)',      // Red
                                    ],
                                }}
                            />
                        )}

                        {/* Property Marker */}
                        <Marker
                            position={center}
                            icon={{
                                path: google.maps.SymbolPath.CIRCLE,
                                scale: 12,
                                fillColor: '#d4a853',  // Gold
                                fillOpacity: 1,
                                strokeColor: '#ffffff',
                                strokeWeight: 3,
                            }}
                            zIndex={1000}
                        />
                    </GoogleMap>
                )}
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="p-3 bg-graphite rounded border border-slate-dark">
                    <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-gold" />
                        <span className="text-sm uppercase tracking-[0.12em] text-stone-700">
                            Nearby Permits
                        </span>
                    </div>
                    <p className="text-2xl font-bold font-mono text-ivory">
                        {nearbyPermitCount}
                    </p>
                    <p className="text-sm text-stone-700 mt-0.5">
                        Within 0.25 miles
                    </p>
                </div>

                <div className="p-3 bg-graphite rounded border border-slate-dark">
                    <div className="flex items-center gap-2 mb-1">
                        <Thermometer className="w-4 h-4 text-gold" />
                        <span className="text-sm uppercase tracking-[0.12em] text-stone-700">
                            Classification
                        </span>
                    </div>
                    <p className={`text-lg font-bold ${config.color}`}>
                        {config.label}
                    </p>
                    <p className="text-sm text-stone-700 mt-0.5">
                        {config.description}
                    </p>
                </div>
            </div>

            {/* Real Permit Intelligence (Phase 142) */}
            {permitIntelligence && (permitIntelligence.renovation_ratio != null || permitIntelligence.permit_lag_correlation != null) && (
                <div className="mt-4 pt-3 border-t border-slate-dark space-y-3">
                    <p className="text-sm uppercase tracking-[0.12em] text-stone-700">
                        Submarket Permit Intelligence
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        {permitIntelligence.renovation_ratio != null && (
                            <div className="p-2.5 bg-graphite rounded border border-slate-dark">
                                <div className="text-sm uppercase tracking-wider text-stone-700">Renovation Ratio</div>
                                <div className="text-lg font-bold font-mono text-gold">{(permitIntelligence.renovation_ratio * 100).toFixed(0)}%</div>
                                <div className="text-sm text-stone-700">of permits are renovations</div>
                            </div>
                        )}
                        {permitIntelligence.permit_density_per_1000 != null && (
                            <div className="p-2.5 bg-graphite rounded border border-slate-dark">
                                <div className="text-sm uppercase tracking-wider text-stone-700">Permit Density</div>
                                <div className="text-lg font-bold font-mono text-ivory">{permitIntelligence.permit_density_per_1000.toFixed(2)}</div>
                                <div className="text-sm text-stone-700">per 1,000 housing units</div>
                            </div>
                        )}
                    </div>
                    {permitIntelligence.permit_lag_months != null && permitIntelligence.permit_lag_correlation != null && (
                        <div className="p-2.5 bg-graphite rounded border border-slate-dark">
                            <div className="text-sm uppercase tracking-wider text-stone-700 mb-1">Predictive Signal</div>
                            <p className="text-sm text-stone-300">
                                Permits predict rent growth <span className="text-gold font-mono font-semibold">{permitIntelligence.permit_lag_months} months</span> ahead
                                {' '}(r={permitIntelligence.permit_lag_correlation > 0 ? '+' : ''}{permitIntelligence.permit_lag_correlation.toFixed(3)})
                            </p>
                        </div>
                    )}
                    {permitIntelligence.classification_breakdown && (
                        <div className="p-2.5 bg-graphite rounded border border-slate-dark">
                            <div className="text-sm uppercase tracking-wider text-stone-700 mb-2">Permit Classification</div>
                            <div className="space-y-1.5">
                                {Object.entries(permitIntelligence.classification_breakdown).map(([type, count]) => {
                                    const total = Object.values(permitIntelligence.classification_breakdown!).reduce((a, b) => a + b, 0);
                                    const pct = total > 0 ? (count / total) * 100 : 0;
                                    return (
                                        <div key={type} className="flex items-center gap-2">
                                            <span className="text-sm text-stone-700 w-28 capitalize">{type.replace('_', ' ')}</span>
                                            <div className="flex-1 h-2 bg-charcoal rounded-full overflow-hidden">
                                                <div className="h-full bg-gold/60 rounded-full" style={{ width: `${pct}%` }} />
                                            </div>
                                            <span className="text-sm font-mono text-stone-300 w-8 text-right">{count}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Legend */}
            <div className="mt-4 pt-3 border-t border-slate-dark">
                <p className="text-sm uppercase tracking-[0.12em] text-stone-700 mb-2">
                    Density Legend
                </p>
                <div className="flex items-center gap-1">
                    <div className="flex-1 h-2 rounded-l bg-gradient-to-r from-emerald via-yellow-400 to-red-500" />
                </div>
                <div className="flex justify-between text-sm text-stone-700 mt-1">
                    <span>Low Activity</span>
                    <span>High Activity</span>
                </div>
            </div>

            {/* Investment Note */}
            {densityClassification === 'cold_spot' && (
                <div className="mt-4 p-3 bg-emerald/10 border border-emerald/30 rounded">
                    <p className="text-sm text-emerald">
                        <strong>Value-Add Opportunity:</strong> Low permit activity suggests this property is ahead of the investment wave. Consider this a contrarian indicator.
                    </p>
                </div>
            )}
        </div>
    );
};

export default PermitHeatmap;
